import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { apiRequest } from '../api/client.js';
import { LanguageSwitcher } from '../../../shared/i18n/LanguageSwitcher.js';
import { useTenantIdentity } from './useTenantIdentity.js';
import { IconMenu, IconSearch, IconChevronDown, IconLogout, IconSwitch, IconBell } from './icons.js';

export interface TopbarProps {
  readonly env: string;
  readonly version: string;
  readonly onOpenPalette: () => void;
  readonly onToggleSidebar: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Topbar({
  env,
  version,
  onOpenPalette,
  onToggleSidebar,
}: TopbarProps): React.ReactElement {
  const { user, token, logout } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const displayName = user?.displayName ?? 'Guest';
  const tenant = useTenantIdentity(token);
  const orgName = tenant?.companyName ?? tenant?.tenantName ?? null;
  const appName = tenant?.applicationName ?? 'ABWMS';
  const isProd = env === 'production';
  return (
    <header role="banner" className="tl-topbar no-print">
      <button
        type="button"
        aria-label="Toggle navigation"
        data-testid="topbar-toggle-sidebar"
        onClick={onToggleSidebar}
        className="tl-iconbtn"
      >
        <IconMenu size={20} />
      </button>
      <Link to="/dashboard" data-testid="topbar-logo" className="tl-brand">
        <span className="tl-brand__word">{appName}</span>
        <span data-testid="topbar-version" className="tl-badge-ver">
          v{version}
        </span>
      </Link>
      {orgName ? (
        <span
          data-testid="topbar-org"
          aria-label={`organisation ${orgName}`}
          // The bar is a single 56px row; a long tenant name must ellipsize
          // rather than wrap, or it pushes down over the sidebar.
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, marginInlineStart: '0.75rem', minWidth: 0, maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden' }}
          title={orgName}
        >
          {tenant?.logoDataUrl ? (
            <img
              src={tenant.logoDataUrl}
              alt={`${orgName} logo`}
              data-testid="topbar-org-logo"
              style={{ height: 32, width: 'auto', maxWidth: 120, objectFit: 'contain', display: 'block', flex: 'none' }}
            />
          ) : null}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orgName}</span>
          {tenant?.tenantCode ? (
            <span style={{ opacity: 0.7, fontWeight: 400, flex: 'none' }}> ({tenant.tenantCode})</span>
          ) : null}
        </span>
      ) : null}
      <span
        data-testid="topbar-env"
        className="tl-badge-env"
        style={{
          background: isProd ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)',
          color: isProd ? 'var(--color-danger)' : 'var(--color-warning)',
        }}
      >
        {env}
      </span>
      <button
        type="button"
        onClick={onOpenPalette}
        data-testid="topbar-command-palette"
        title="Command palette (Ctrl+K)"
        aria-label="Search or jump to"
        className="tl-search"
      >
        <IconSearch size={16} />
        <span className="tl-search__label">Search or jump to…</span>
        <span className="tl-search__kbd" aria-hidden="true">Ctrl K</span>
      </button>
      <div className="tl-topbar__spacer" />
      <NotificationBell />
      <LanguageSwitcher />
      <div className="tl-pop">
        <button
          type="button"
          data-testid="topbar-profile"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="tl-profile"
        >
          <span className="tl-avatar" aria-hidden="true">{initials(displayName)}</span>
          <span className="tl-profile__name">{displayName}</span>
          <IconChevronDown size={16} />
        </button>
        {menuOpen && (
          <div role="menu" data-testid="topbar-profile-menu" className="tl-menu">
            <Link
              to="/profile"
              role="menuitem"
              data-testid="my-profile"
              onClick={() => setMenuOpen(false)}
              className="tl-menu__item"
            >
              <IconSwitch size={16} />
              My profile
            </Link>
            <button
              type="button"
              role="menuitem"
              data-testid="sign-out"
              onClick={() => {
                logout();
                setMenuOpen(false);
              }}
              className="tl-menu__item"
            >
              <IconLogout size={16} />
              Sign out
            </button>
          </div>
        )}
      </div>
      <LocationBadge />
    </header>
  );
}

function LocationBadge(): React.ReactElement {
  const loc = useLocation();
  return (
    <span aria-hidden="true" style={{ display: 'none' }} data-current-path={loc.pathname} />
  );
}

const UNREAD_POLL_MS = 60_000;

/**
 * Unread count on the bell. Polled rather than pushed: there is no socket in
 * the shell, and an approver who never reloads the page would otherwise never
 * learn that work arrived.
 */
function NotificationBell(): React.ReactElement | null {
  const { token } = useAuth();
  const loc = useLocation();
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!token) { setCount(0); return; }
    let alive = true;
    const read = () => {
      apiRequest<{ data: { unreadCount: number } }>('/notifications/unread-count', { token })
        .then((r) => { if (alive) setCount(r.data?.unreadCount ?? 0); })
        // A missing count must never take the shell down.
        .catch(() => { /* leave the last known count in place */ });
    };
    read();
    const timer = window.setInterval(read, UNREAD_POLL_MS);
    const onFocus = () => read();
    window.addEventListener('focus', onFocus);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
    // Re-read on navigation so opening the inbox clears the badge promptly.
  }, [token, loc.pathname]);

  const label = count > 0 ? `Notifications, ${count} unread` : 'Notifications';
  return (
    <Link
      to="/inbox/notifications"
      className="tl-iconbtn"
      data-testid="topbar-notifications"
      aria-label={label}
      title={label}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <IconBell size={20} />
      {count > 0 ? (
        <span
          data-testid="topbar-unread-count"
          style={{
            position: 'absolute',
            top: 0,
            insetInlineEnd: 0,
            transform: 'translate(35%, -25%)',
            minWidth: 18,
            height: 18,
            padding: '0 4px',
            borderRadius: 999,
            background: 'var(--color-danger, #c5221f)',
            color: '#fff',
            fontSize: 11,
            lineHeight: '18px',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
}
